import { Injectable } from '@angular/core';
import { delay, Observable, of } from 'rxjs';
import { faker } from '@faker-js/faker';
import { HttpEvent, HttpRequest, HttpResponse } from '@angular/common/http';
import {
  AdminTenantDetail,
  AdminTenantsPageResponse,
  TenantStatus,
  UpdateTenantDetailsDto,
  UpdateTenantStatusDto,
} from '@app/core/models/admin-tenant.interface';

@Injectable({ providedIn: 'root' })
export class AdminTenantMocksService {
  private readonly STORAGE_KEY = 'mock_admin_tenants';
  private tenants: AdminTenantDetail[] = [];

  constructor() {
    this.loadFromStorage();
    if (this.tenants.length === 0) {
      this.generateMockTenants();
      this.saveToStorage();
    }
  }

  handleMockRequest(req: HttpRequest<any>): Observable<HttpEvent<any>> {
    const urlParts = req.url.split('/');
    const adminIndex = urlParts.indexOf('admin');
    // segments after 'admin': ['tenants'] or ['tenants', ':id'] or ['tenants', ':id', 'status']
    const afterAdmin = urlParts.slice(adminIndex + 1);
    const tenantIdStr = afterAdmin[1];
    const tenantId = tenantIdStr ? parseInt(tenantIdStr, 10) : undefined;
    const isStatusEndpoint = afterAdmin[2] === 'status';

    if (req.method === 'GET') {
      if (tenantId) {
        return this.getTenant(tenantId);
      } else {
        return this.listTenants(req);
      }
    } else if (req.method === 'PUT' && tenantId && !isStatusEndpoint) {
      return this.updateTenant(tenantId, req.body as UpdateTenantDetailsDto);
    } else if (req.method === 'PATCH' && tenantId && isStatusEndpoint) {
      return this.updateTenantStatus(tenantId, req.body as UpdateTenantStatusDto);
    }

    throw new Error(`Unsupported mock admin-tenant request: ${req.method} ${req.url}`);
  }

  private listTenants(req: HttpRequest<any>): Observable<HttpEvent<any>> {
    const url = new URL(req.url);
    const page = parseInt(url.searchParams.get('page') || '1', 10);
    const limit = parseInt(url.searchParams.get('limit') || '10', 10);
    const q = url.searchParams.get('q') || '';
    const status = url.searchParams.get('status') as TenantStatus | null;

    const filtered = this.tenants.filter((t) => {
      const matchesQuery =
        !q ||
        t.name.toLowerCase().includes(q.toLowerCase()) ||
        (t.email && t.email.toLowerCase().includes(q.toLowerCase())) ||
        (t.phone && t.phone.includes(q));
      const matchesStatus = !status || t.status === status;
      return matchesQuery && matchesStatus;
    });

    const total = filtered.length;
    const data = filtered.slice((page - 1) * limit, page * limit);

    const response: AdminTenantsPageResponse = {
      data,
      total,
      page,
      limit,
      totalPages: Math.ceil(total / limit),
    };

    return this.respond(response, 200);
  }

  private getTenant(tenantId: number): Observable<HttpEvent<any>> {
    const tenant = this.tenants.find((t) => t.tenantId === tenantId);
    if (tenant) {
      return this.respond(tenant, 200);
    }
    return this.respond({ message: 'Tenant not found' }, 404);
  }

  private updateTenant(tenantId: number, dto: UpdateTenantDetailsDto): Observable<HttpEvent<any>> {
    const index = this.tenants.findIndex((t) => t.tenantId === tenantId);
    if (index === -1) return this.respond({ message: 'Tenant not found' }, 404);
    this.tenants[index] = {
      ...this.tenants[index],
      ...dto,
      tradingAs: dto.tradingAs ?? this.tenants[index].tradingAs,
      email: dto.email ?? this.tenants[index].email,
      phone: dto.phone ?? this.tenants[index].phone,
      address: dto.address ?? this.tenants[index].address,
      gstNumber: dto.gstNumber ?? this.tenants[index].gstNumber,
      paymentInstructions: dto.paymentInstructions ?? this.tenants[index].paymentInstructions,
      updatedAt: new Date().toISOString(),
    };
    this.saveToStorage();
    return this.respond(this.tenants[index], 200);
  }

  private updateTenantStatus(
    tenantId: number,
    dto: UpdateTenantStatusDto
  ): Observable<HttpEvent<any>> {
    const index = this.tenants.findIndex((t) => t.tenantId === tenantId);
    if (index === -1) return this.respond({ message: 'Tenant not found' }, 404);
    this.tenants[index] = {
      ...this.tenants[index],
      status: dto.status,
      updatedAt: new Date().toISOString(),
    };
    this.saveToStorage();
    return this.respond(this.tenants[index], 200);
  }

  private respond<T>(body: T, status: number): Observable<HttpEvent<T>> {
    return of(new HttpResponse({ status, body })).pipe(delay(400));
  }

  private loadFromStorage(): void {
    try {
      const stored = localStorage.getItem(this.STORAGE_KEY);
      if (stored) {
        this.tenants = JSON.parse(stored);
      }
    } catch {
      this.tenants = [];
    }
  }

  private saveToStorage(): void {
    try {
      localStorage.setItem(this.STORAGE_KEY, JSON.stringify(this.tenants));
    } catch {
      // ignore storage errors
    }
  }

  private generateMockTenants(): void {
    const statuses: TenantStatus[] = ['ACTIVE', 'INACTIVE'];
    for (let i = 1; i <= 20; i++) {
      const createdAt = faker.date.past({ years: 2 }).toISOString();
      this.tenants.push({
        tenantId: i,
        name: faker.company.name(),
        tradingAs: faker.datatype.boolean() ? faker.company.name() : null,
        email: faker.internet.email(),
        phone: this.generateNZPhone(),
        address: this.generateNZAddress(),
        gstNumber: faker.datatype.boolean()
          ? faker.string.numeric(3) + '-' + faker.string.numeric(3) + '-' + faker.string.numeric(3)
          : null,
        paymentInstructions: faker.datatype.boolean()
          ? `Direct credit to BNZ ${faker.finance.accountNumber(15)}\nReference: invoice number.`
          : null,
        smsEnabled: faker.datatype.boolean(),
        status: faker.helpers.arrayElement(statuses),
        createdAt,
        updatedAt: faker.date.recent({ days: 30 }).toISOString(),
      });
    }
  }

  private generateNZPhone(): string {
    const prefixes = ['021', '022', '027', '09', '04', '03'];
    const prefix = faker.helpers.arrayElement(prefixes);
    const number = faker.string.numeric(7);
    return `+64 ${prefix} ${number.substring(0, 3)} ${number.substring(3)}`;
  }

  private generateNZAddress(): string {
    const cities = ['Auckland', 'Wellington', 'Christchurch', 'Hamilton', 'Tauranga', 'Dunedin'];
    const streetTypes = ['Street', 'Road', 'Avenue', 'Drive', 'Lane'];
    return `${faker.number.int({ min: 1, max: 999 })} ${faker.person.lastName()} ${faker.helpers.arrayElement(streetTypes)}, ${faker.helpers.arrayElement(cities)}`;
  }
}
